import React, { useState } from 'react';
import { ArrowRightIcon, SunIcon } from 'lucide-react';
import { EMAIL_ENDPOINT } from '../data/links';
interface GateProps {
  onUnlock: (email: string) => void;
}
export function record(email: string, source: string, extra?: string) {
  if (!EMAIL_ENDPOINT) return;
  try {
    fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      mode: 'no-cors',
      body: JSON.stringify({
        email,
        source,
        extra,
        at: new Date().toISOString()
      })
    }).catch(() => {});
  } catch {
    /* recording an email must never block the customer */
  }
}

/* One field. No PIN, no code, no password. Any address gets in.
   The email is here to reach the customer later, not to check them at the
   door: a door that can reject a paying customer is worse than an open one. */
export function Gate({
  onUnlock
}: GateProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setError('Please enter the email address you ordered with.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('That email address does not look right.');
      return;
    }
    setError(null);
    record(cleanEmail, 'gate');
    onUnlock(cleanEmail);
  };
  return <main className="flex min-h-screen w-full flex-col justify-between bg-paper px-5 py-8 sm:px-8 sm:py-10" data-id="element-131" data-mp="2vldn98ru5sfm">
      <header className="mx-auto w-full max-w-portal" data-id="element-132" data-mp="2rkldmef1xlit">
        <div className="flex items-center gap-2" data-id="element-133" data-mp="2rm4ti3wspuk2">
          <SunIcon aria-hidden="true" className="h-4 w-4 text-sun" strokeWidth={2.5} data-id="element-134" data-mp="35k37373almyi" />
          <span className="font-display text-[14px] font-semibold uppercase tracking-[0.14em] text-navy" data-id="element-135" data-mp="30k8ff1bp0tce">
            Little Engineers
          </span>
        </div>
      </header>

      <div className="mx-auto w-full max-w-[30rem] py-12" data-id="element-136" data-mp="166pav7j8wahl">
        <div className="rounded-[18px] border border-navy/10 bg-white p-7 sm:p-9" data-id="element-137" data-mp="168a66zddvxgi">
          <h1 className="font-display text-[34px] font-bold leading-[1.08] text-navy sm:text-[40px]" data-id="element-138" data-mp="36a061sgwufvz">
            Open your library
          </h1>
          <p className="mt-4 font-sans text-[16px] leading-relaxed text-navy/70" data-id="element-139" data-mp="3b9wblw59afdn">
            Your Build &amp; Discover collection, plus your original library and extras. Enter the
            email you ordered with to get started.
          </p>

          <form onSubmit={handleSubmit} noValidate className="mt-8" data-id="element-140" data-mp="3p6rq444a0z13">
            <label htmlFor="email" className="block font-sans text-[12px] font-semibold uppercase tracking-[0.12em] text-navy/55" data-id="element-141" data-mp="3u6ntq292aog4">
              Email address
            </label>
            <input id="email" name="email" type="email" value={email} autoComplete="email" autoFocus placeholder="you@example.com" aria-invalid={Boolean(error)} aria-describedby={error ? 'email-error' : 'email-hint'} onChange={event => {
            setEmail(event.target.value);
            if (error) setError(null);
          }} className={`mt-2 w-full rounded-[12px] border bg-paper px-4 py-3 font-sans text-[16px] text-navy placeholder:text-navy/35 transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 focus-visible:ring-offset-white ${error ? 'border-[#C2482F]' : 'border-navy/15 focus:border-navy/45'}`} data-id="element-142" data-mp="6u3b36do9n5r" />
            {error ? <p id="email-error" role="alert" className="mt-2 font-sans text-[13px] leading-relaxed text-[#C2482F]" data-id="element-143" data-mp="zv33z052tcbe">
                {error}
              </p> : <p id="email-hint" className="mt-2 font-sans text-[13px] text-navy/45" data-id="element-144" data-mp="1nm8j1d3700kn">
                No password and no code. We use it to reach you about your order, nothing else.
              </p>}

            <button type="submit" className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-[18px] bg-sun px-6 py-4 font-display text-[16px] font-semibold text-navy transition-colors duration-150 ease-out hover:bg-[#F2B01C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 focus-visible:ring-offset-white" data-id="element-145" data-mp="1d3tjpmxb5aux">
              Enter my library
              <ArrowRightIcon aria-hidden="true" className="h-4 w-4" strokeWidth={2.5} data-id="element-146" data-mp="1vt055hphkgkf" />
            </button>
          </form>
        </div>
      </div>

      <footer className="mx-auto w-full max-w-portal border-t border-navy/10 pt-5" data-id="element-147" data-mp="1m3gkc6i7qcmx">
        <p className="font-sans text-[13.5px] leading-relaxed text-navy/55" data-id="element-148" data-mp="29tt2sbb33rrr">
          Trouble getting in? Reply to your order confirmation email with your order number and
          tell us what happened.
        </p>
      </footer>
    </main>;
}